'use client';

import { useRef, useEffect, useState } from 'react';
import type { PortableTextBlock } from '@portabletext/types';
import DownloadButton from 'components/controls/links/download-button';
import DownloadIcon from 'assets/icons/download.svg';
import ChevronDown from 'assets/icons/chevron-down.svg';
import RichText from 'components/rich-text';
import { humanizeFileSize } from 'helpers/helpers';
import styles from './download-list-item.module.scss';

export interface DownloadFormat {
	_key: string;
	asset?: {
		url: string;
		size?: number;
		originalFilename?: string;
		extension?: string;
	};
}

type PopulatedDownloadFormat = DownloadFormat & {
	asset: NonNullable<DownloadFormat['asset']>;
};

export interface DownloadListItemData {
	title: string;
	description?: PortableTextBlock[];
	formats?: DownloadFormat[];
}

export interface IProps {
	item: DownloadListItemData;
	sizePrecision?: number;
}

const hasAsset = (format: DownloadFormat): format is PopulatedDownloadFormat =>
	!!format.asset?.url;

const getFormatLabel = (format: PopulatedDownloadFormat): string => {
	const ext =
		format.asset.extension ??
		format.asset.originalFilename?.split('.').pop()?.toLowerCase() ??
		format.asset.url.split('.').pop()?.split('?')[0]?.toLowerCase();
	return ext ? ext.toUpperCase() : 'File';
};

const DownloadListItem = ({ item, sizePrecision = 1 }: IProps) => {
	const { title, description, formats } = item;
	const populatedFormats = formats?.filter(hasAsset) ?? [];
	const [open, setOpen] = useState(false);
	const wrapperRef = useRef<HTMLDivElement>(null);

	useEffect(() => {
		const handleClickOutside = (e: MouseEvent) => {
			if (
				wrapperRef.current &&
				!wrapperRef.current.contains(e.target as Node)
			) {
				setOpen(false);
			}
		};
		document.addEventListener('mousedown', handleClickOutside);
		return () => document.removeEventListener('mousedown', handleClickOutside);
	}, []);

	const isSingleFormat = populatedFormats.length === 1;

	return (
		<li className={styles.item}>
			<div className={styles.content}>
				<div className={styles.title}>{title}</div>
				{description && (
					<div className={styles.description}>
						<RichText value={description} />
					</div>
				)}
			</div>
			{!!populatedFormats.length && (
				<div className={styles.actions} ref={wrapperRef}>
					{isSingleFormat ? (
						<DownloadButton
							file={populatedFormats[0].asset}
							text={`Download ${getFormatLabel(populatedFormats[0])}`}
							sizePrecision={sizePrecision}
							target="_blank"
							rel="noopener noreferrer"
						/>
					) : (
						<div className={styles.dropdown}>
							<button
								className={`button tertiary ${styles.dropdownTrigger}`}
								aria-haspopup="true"
								aria-expanded={open}
								onClick={() => setOpen(!open)}
							>
								Download
								<DownloadIcon />
								<ChevronDown
									className={`${styles.chevron} ${open ? styles.chevronOpen : ''}`}
								/>
							</button>
							{open && (
								<ul className={styles.dropdownMenu}>
									{populatedFormats.map((format) => (
										<li key={format._key}>
											<a
												href={format.asset.url}
												className={styles.dropdownItem}
												target="_blank"
												rel="noopener noreferrer"
											>
												<strong>{getFormatLabel(format)}</strong>
												{format.asset.size && (
													<span className={styles.dropdownItemSize}>
														(
														{humanizeFileSize(format.asset.size, sizePrecision)}
														)
													</span>
												)}
											</a>
										</li>
									))}
								</ul>
							)}
						</div>
					)}
				</div>
			)}
		</li>
	);
};

export default DownloadListItem;
